/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import * as React from 'react';
import { postFormDataAsync } from '../../actions/DataActionExtension.g';
import { IFormSubmissionValue } from '../../actions/DataServiceEntities.g';
import { normalizeName } from '../../utilities/normalizeName';
import { FormProvider, IFieldValidation, IFormField } from './context/FormContext';

import { IEveFormData } from './eve-form.data';
import { IEveFormProps } from './eve-form.props.autogenerated';

export interface IEveFormViewProps extends IEveFormProps<IEveFormData> {
    handleSubmit: () => {};
    resetForm: () => {};
    statusMessage: string;
}

export interface IEveFormState {
    fields: Record<string, IFormField>;
    statusMessage: string;
}

/**
 *
 * EveForm component
 * @extends {React.PureComponent<IEveFormProps<IEveFormData>>}
 */
class EveForm extends React.PureComponent<IEveFormProps<IEveFormData>, IEveFormState> {
    constructor(props: Readonly<IEveFormProps<IEveFormData>>) {
        super(props);

        this.state = { fields: {}, statusMessage: '' };

        this.registerField = this.registerField.bind(this);
        this.updateField = this.updateField.bind(this);
        this.resetForm = this.resetForm.bind(this);
        this.handleSubmit = this.handleSubmit.bind(this);
    }

    /**
     * Registers a field to context so it can be updated and read by the form.
     * @param fieldData
     */
    private registerField(fieldData: IFormField): void {
        const fieldName = normalizeName(fieldData.name);
        if (!(fieldName in this.state.fields)) {
            this.setState(prevState => ({
                fields: {
                    ...prevState.fields,
                    [fieldName]: fieldData
                }
            }));
        }
    }

    /**
     * When called, updates the field with the given name to have the value that is given.
     * @param fieldName
     * @param fieldValue
     * @returns
     */
    private updateField(fieldName: string, fieldValue: string, friendlyName: string) {
        const normalizedName = normalizeName(fieldName);
        if (!(normalizedName in this.state.fields)) {
            return;
        }

        const fields = { ...this.state.fields };
        const fieldToModify = fields[normalizedName];
        fields[normalizedName] = {
            ...fieldToModify,
            value: fieldValue,
            friendly: friendlyName,
            errors: []
        };

        this.setState({ fields });
    }

    /**
     * Handles the submission of the form. Validates all fields and then makes CSU call if all fields pass validation.
     * @returns void
     */
    private handleSubmit(): void {
        // validate
        const fields = { ...this.state.fields };
        const keys = Object.keys(fields);
        let hasErrors: boolean = false;
        keys.forEach(key => {
            const f = fields[key];
            const fieldErrors = this.validateField(f.validation, f.value);
            if (fieldErrors.length) {
                hasErrors = true;
            }
            fields[key] = {
                ...f,
                errors: fieldErrors
            };
        });

        if (hasErrors) {
            this.setState({ fields, statusMessage: this.props.resources.validationFailMessage });
            return;
        }

        const formValues: IFormSubmissionValue[] = [];
        keys.forEach(key => {
            const f = fields[key];
            formValues.push({ Name: key, Value: f.value, Friendly: f.friendly });
        });

        postFormDataAsync(
            { callerContext: this.props.context.actionContext },
            'eve',
            formValues,
            this.props.config.formTitle ? this.props.config.formTitle : '',
            this.props.config.mailTo ? this.props.config.mailTo : ''
        ).then(response => {
            if (response.Success) {
                this.setState({ statusMessage: this.props.resources.successMessage });
                this.resetForm();
            } else {
                this.setState({ statusMessage: this.props.resources.failMessage });
            }
        });
    }

    /**
     * Validates the given field with the given validation objects. Returns an array of strings containing error messages.
     * @param validationRules
     * @param fieldValue
     * @returns string []
     */
    private validateField(validationRules: IFieldValidation[], fieldValue: string): string[] {
        const errors: string[] = [];
        validationRules.forEach(rule => {
            if (rule.isRequiredValidation) {
                if (!fieldValue) {
                    errors.push(rule.errorMessage);
                }
            } else if (rule.invalidSelection && rule.invalidSelection === fieldValue) {
                errors.push(rule.errorMessage);
            }

            if (errors.length === 0) {
                const regex = new RegExp(rule.regex, 'g');
                const isValid = regex.test(`${fieldValue}`);
                if (!isValid) {
                    errors.push(rule.errorMessage);
                }
            }
        });
        return errors;
    }

    /**
     * resets all fields in the form to their default state
     */
    private resetForm() {
        const fields = { ...this.state.fields };
        for (const key in fields) {
            if (fields.hasOwnProperty(key)) {
                fields[key].reset();
            }
        }
        this.setState({ statusMessage: '', fields });
    }

    /**
     * This renders the module.
     * @returns JSX.Element | Null
     */
    public render(): JSX.Element | null {
        const { registerField, updateField } = this;
        const { fields } = this.state;
        return (
            <FormProvider value={{ fields, registerField, updateField }}>
                {this.props.renderView({
                    ...this.props,
                    handleSubmit: this.handleSubmit,
                    resetForm: this.resetForm,
                    statusMessage: this.state.statusMessage
                })}
            </FormProvider>
        );
    }
}

export default EveForm;
